// Tiny WebAudio synth so the playground actually makes sound.
// Maps a line's wavelength (380–700 nm) to a pitch (logarithmic, 110–1760 Hz,
// inverted so longer wavelength = lower pitch). Uses V (finger Y) for gain
// and filter cutoff.

const MIN_FREQ = 110;
const MAX_FREQ = 1760;

interface Voice {
  osc: OscillatorNode;
  gain: GainNode;
  filter: BiquadFilterNode;
}

export class AudioToy {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private voices = new Map<string, Voice>(); // key=`${finger}.${line}`

  constructor() {
    // Create the AudioContext on the very first user gesture anywhere on the
    // page. Some browsers (Chrome) print a warning if AudioContext is created
    // outside a gesture even if it ends up suspended.
    const onFirstGesture = () => {
      this.ensureCtxSync();
      window.removeEventListener("pointerdown", onFirstGesture, true);
      window.removeEventListener("touchstart", onFirstGesture, true);
      window.removeEventListener("keydown", onFirstGesture, true);
    };
    window.addEventListener("pointerdown", onFirstGesture, true);
    window.addEventListener("touchstart", onFirstGesture, true);
    window.addEventListener("keydown", onFirstGesture, true);
  }

  private ensureCtxSync(): AudioContext {
    if (this.ctx && this.ctx.state !== "closed") {
      if (this.ctx.state === "suspended") void this.ctx.resume();
      return this.ctx;
    }
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctx();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.18;
    this.master.connect(this.ctx.destination);
    return this.ctx;
  }

  private wavelengthToFreq(wavelengthNm: number): number {
    const t = Math.max(0, Math.min(1, (wavelengthNm - 380) / (700 - 380)));
    const logMin = Math.log(MIN_FREQ);
    const logMax = Math.log(MAX_FREQ);
    return Math.exp(logMin + (1 - t) * (logMax - logMin));
  }

  noteOn(line: number, finger: number, wavelengthNm: number) {
    const ctx = this.ensureCtxSync();
    if (ctx.state === "suspended") void ctx.resume();
    if (!this.master) return;
    const key = `${finger}.${line}`;
    this.noteOff(line, finger);

    const osc = ctx.createOscillator();
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.value = this.wavelengthToFreq(wavelengthNm);
    filter.type = "lowpass";
    filter.frequency.value = 4000;
    filter.Q.value = 1.5;
    gain.gain.value = 0;
    osc.connect(filter).connect(gain).connect(this.master);
    osc.start();

    const now = ctx.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.6, now + 0.015);

    this.voices.set(key, { osc, gain, filter });
  }

  setUV(line: number, finger: number, _u: number, v: number) {
    const ctx = this.ctx;
    if (!ctx) return;
    const voice = this.voices.get(`${finger}.${line}`);
    if (!voice) return;
    const now = ctx.currentTime;
    const target = 0.15 + v * 0.85;
    voice.gain.gain.setTargetAtTime(target * 0.6, now, 0.04);
    voice.filter.frequency.setTargetAtTime(800 + v * 6000, now, 0.04);
  }

  setMuted(muted: boolean) {
    if (this.master) this.master.gain.value = muted ? 0 : 0.18;
  }

  noteOff(line: number, finger: number) {
    const ctx = this.ctx;
    if (!ctx) return;
    const key = `${finger}.${line}`;
    const voice = this.voices.get(key);
    if (!voice) return;
    const now = ctx.currentTime;
    voice.gain.gain.cancelScheduledValues(now);
    voice.gain.gain.setValueAtTime(voice.gain.gain.value, now);
    voice.gain.gain.linearRampToValueAtTime(0, now + 0.12);
    voice.osc.stop(now + 0.14);
    this.voices.delete(key);
  }
}
